import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CargoQuoteRequest } from '../../types/quotes';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Separator } from '../ui/separator';
import { StatusBadge } from '../shared/StatusBadge';
import { ROUTE_ARROW, formatMeasurement, isUnspecifiedDateType } from '../../lib/format';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';

interface QuotesListProps {
  quotes: CargoQuoteRequest[];
  loading: boolean;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  currentPage: number;
  setCurrentPage: (page: number | ((prev: number) => number)) => void;
  itemsPerPage: number;
  setItemsPerPage: (count: number) => void;
  paginatedItems: CargoQuoteRequest[];
  totalPages: number;
  getPickupDate: (quote: CargoQuoteRequest) => string | null;
  getLastResponseDate: (quote: CargoQuoteRequest) => string | null;
  getPriceRange: (responses: CargoQuoteRequest['quote_responses']) => string;
}

export const QuotesList: React.FC<QuotesListProps> = ({
  loading,
  statusFilter,
  setStatusFilter,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  paginatedItems,
  totalPages,
  getPickupDate,
  getLastResponseDate: _getLastResponseDate,
  getPriceRange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isValidTimestamp = (value?: string | null): value is string =>
    typeof value === 'string' && value.length > 0 && !Number.isNaN(Date.parse(value));

  const relativeToNow = (value?: string | null): string | null =>
    isValidTimestamp(value) ? formatDistanceToNow(new Date(value), { addSuffix: true }) : null;

  if (loading) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg">{t('quote.list.title')}</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg">{t('quote.list.title')}</h2>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm"
        >
          <option value="">{t('quote.list.status_filter.all')}</option>
          <option value="pending">{t('quote.list.status_filter.pending')}</option>
          <option value="quoted">{t('quote.list.status_filter.quoted')}</option>
          <option value="awaiting_contact">{t('quote.list.status_filter.awaiting_contact')}</option>
          <option value="contacted">{t('quote.list.status_filter.contacted')}</option>
          <option value="rejected">{t('quote.list.status_filter.rejected')}</option>
          <option value="canceled">{t('quote.list.status_filter.canceled')}</option>
        </select>
      </div>

      <div className="grid gap-2">
        {paginatedItems.map((quote) => {
          const pickupDate = getPickupDate(quote);
          const quoteAge = relativeToNow(quote.created_at);

          return (
            <Card
              key={quote.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/dashboard/quotes/${quote.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    {quote.cargo_title ? (
                      <h3 className="text-lg font-semibold text-slate-900 truncate">
                        {quote.cargo_title}
                      </h3>
                    ) : null}
                    <p
                      className={`text-base font-medium text-slate-900 ${
                        quote.cargo_title ? 'mt-0.5' : ''
                      }`}
                    >
                      {quote.origin_city} {ROUTE_ARROW} {quote.destination_city}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      {quote.tracking_number}
                      {pickupDate
                        ? ` · ${pickupDate}`
                        : isUnspecifiedDateType(quote.date_type)
                          ? ` · ${t('quote.list.no_specific_date')}`
                          : ''}
                      {quoteAge ? ` · ${quoteAge}` : ''}
                    </p>
                  </div>
                  <StatusBadge
                    status={quote.status}
                    timestamp={quote.updated_at}
                    tPrefix="quote.status"
                    className="shrink-0 self-start"
                  />
                </div>
              </CardHeader>

              <Separator decorative={false} />

              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row justify-between gap-3">
                  <div className="text-sm space-y-1">
                    <p className="text-slate-600">
                      {formatMeasurement(quote.cargo_length)}×{formatMeasurement(quote.cargo_width)}×{formatMeasurement(quote.cargo_height)} cm
                    </p>
                    <p className="text-slate-600">{formatMeasurement(quote.cargo_weight)} kg</p>
                    <p className="text-slate-600">
                      {t('quote.list.card.volume', {
                        volume: quote.cargo_volume_m3,
                      })}
                    </p>
                  </div>
                  <div className="text-sm sm:text-right">
                    {quote.quote_responses.length > 0 ? (
                      <span className="font-medium">
                        {getPriceRange(quote.quote_responses)}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic">
                        {t('quote.list.card.no_price')}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 bg-white rounded-lg border shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t('common.items_per_page')}:
          </span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="h-8 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t('common.page_info', { current: currentPage, total: totalPages || 1 })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            {t('common.previous')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            {t('common.next')}
          </Button>
        </div>
      </div>
    </div>
  );
};
